import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Receipt, X } from "lucide-react";
import {
  loadDraft, clearDraft, DRAFT_EVENT, DRAFT_RESUME_EVENT,
} from "../lib/quotationDraft";

const QUOTE_PATH = "/admin/estimated-quotations";

/**
 * Gmail-style minimized "compose" bar. Lives in AdminLayout so it stays
 * visible on every admin page. Shows when an Estimated Quotation draft exists
 * and the quotation popup is not currently open on screen.
 */
export default function QuotationDraftBar() {
  const [draft, setDraft] = useState(() => loadDraft());
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const sync = () => setDraft(loadDraft());
    window.addEventListener(DRAFT_EVENT, sync);
    window.addEventListener("storage", sync); // other browser tabs
    return () => {
      window.removeEventListener(DRAFT_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  if (!draft) return null;

  const onQuotePage = pathname.startsWith(QUOTE_PATH);
  // Hide only while the popup itself is open on the quotations page.
  if (onQuotePage && draft.open) return null;

  const resume = () => {
    if (onQuotePage) {
      window.dispatchEvent(new Event(DRAFT_RESUME_EVENT));
    } else {
      navigate(`${QUOTE_PATH}?resume=1`);
    }
  };

  const discard = () => {
    if (window.confirm("Draft discard kar dein? Saved data hat jaayega.")) clearDraft();
  };

  const name = (draft.form.buyer_name || "").trim();
  const title = draft.editingId ? "Editing quotation" : "New quotation (draft)";

  return (
    <div
      className="fixed bottom-0 right-4 md:right-8 z-40 w-[290px] shadow-2xl"
      data-testid="quotation-draft-bar"
    >
      <div className="flex items-center bg-slate-900 text-white rounded-t-md border border-slate-700 border-b-0">
        <button
          type="button"
          onClick={resume}
          className="flex-1 flex items-center gap-2 px-3 py-2.5 text-left min-w-0 hover:bg-slate-800 rounded-tl-md"
          data-testid="quotation-draft-resume"
          title="Resume draft"
        >
          <Receipt className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="min-w-0">
            <span className="block text-[11px] font-bold uppercase tracking-wider truncate">{title}</span>
            <span className="block text-[11px] text-slate-400 truncate">
              {name || "Untitled"} · click to resume
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={discard}
          className="px-3 py-2.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-tr-md"
          data-testid="quotation-draft-discard"
          title="Discard draft"
          aria-label="Discard draft"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}